import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Alert, Button, Snackbar } from '@mui/material';

/**
 * Update and offline notices for the guest viewer.
 *
 * `registerType: 'prompt'` means a new build never swaps under a guest mid-flip;
 * they are told and asked. On a dining-room tablet that runs for weeks, an
 * unannounced reload in front of a table is the worst possible moment.
 */
export function ServiceWorkerPrompt() {
  const [offline, setOffline] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      // Long-lived tablets never reload on their own, so nothing would ever
      // notice a new build without an explicit poll.
      if (registration) {
        setInterval(() => void registration.update(), 60 * 60 * 1000);
      }
    },
  });

  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return (
    <>
      <Snackbar open={needRefresh} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert
          severity="info"
          variant="filled"
          action={
            <>
              <Button color="inherit" size="small" onClick={() => void updateServiceWorker(true)}>
                Reload
              </Button>
              <Button color="inherit" size="small" onClick={() => setNeedRefresh(false)}>
                Later
              </Button>
            </>
          }
        >
          A new version is ready.
        </Alert>
      </Snackbar>

      <Snackbar
        open={offlineReady}
        autoHideDuration={4000}
        onClose={() => setOfflineReady(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setOfflineReady(false)}>
          This menu now works without a connection.
        </Alert>
      </Snackbar>

      <Snackbar open={offline} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="warning" variant="filled">
          You are offline. Showing the last menu that was loaded.
        </Alert>
      </Snackbar>
    </>
  );
}
